import { useEffect, useMemo, useRef, useState } from "react";

import { downloadBlob } from "./lib/export";
import { LOCAL_TIXPIX } from "./localPrinter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type DitherMethod = "atkinson" | "floyd-steinberg" | "bayer";
type FontFamily = "equity" | "berkeley";
type TextAlign = "left" | "center" | "right";
type TextPosition = "top" | "bottom";
type TextLine = "title" | "details";
type PreviewMode = "pixels" | "print";

type ArtworkRender = {
  id: string;
  url: string;
  width: number;
  height: number;
  blackCoverage: number;
};

type ArtworkOptions = {
  height: number;
  title: string;
  details: string;
  titleFont: FontFamily;
  titleSize: number;
  titleSmallCaps: boolean;
  titleUpper: boolean;
  detailsFont: FontFamily;
  detailsSize: number;
  detailsSmallCaps: boolean;
  detailsUpper: boolean;
  textAlign: TextAlign;
  textPosition: TextPosition;
  showDivider: boolean;
  method: DitherMethod;
  exposure: number;
  contrast: number;
  gamma: number;
  threshold: number;
  scale: number;
  offsetX: number;
  offsetY: number;
  rotation: number;
};

type SavedProject = {
  format: "tixpix-artwork";
  version: 1;
  savedAt: string;
  options: ArtworkOptions;
  source: { name: string; type: string; dataUrl: string } | null;
};

const DEFAULT_OPTIONS: ArtworkOptions = {
  height: 900,
  title: "THE BRIDE & GROOM",
  details: "A WEDDING GIFT  ·  2026",
  titleFont: "equity",
  titleSize: 54,
  titleSmallCaps: true,
  titleUpper: false,
  detailsFont: "equity",
  detailsSize: 31,
  detailsSmallCaps: false,
  detailsUpper: false,
  textAlign: "center",
  textPosition: "bottom",
  showDivider: true,
  method: "atkinson",
  exposure: 0,
  contrast: 1.2,
  gamma: 1,
  threshold: 0.5,
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
};

export function ArtworkView() {
  const [source, setSource] = useState<File | null>(null);
  const [options, setOptions] = useState<ArtworkOptions>(DEFAULT_OPTIONS);
  const [activeLine, setActiveLine] = useState<TextLine>("title");
  const [previewMode, setPreviewMode] = useState<PreviewMode>("pixels");
  const [render, setRender] = useState<ArtworkRender | null>(null);
  const [status, setStatus] = useState("rendering sample…");
  const [rendering, setRendering] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [canPrint, setCanPrint] = useState(true);
  const [count, setCount] = useState(1);
  const projectInput = useRef<HTMLInputElement>(null);

  const patch = <K extends keyof ArtworkOptions>(key: K, value: ArtworkOptions[K]) =>
    setOptions((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    fetch("/api/config")
      .then((response) => response.json())
      .then((config: { canPrint?: boolean }) => setCanPrint(config.canPrint !== false))
      .catch(() => setCanPrint(true));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setRendering(true);
      setStatus("rendering exact printer pixels…");
      try {
        const body = new FormData();
        body.set("options", JSON.stringify(options));
        if (source) body.set("file", source);

        const response = await fetch("/api/artwork/render", {
          method: "POST",
          body,
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(await response.text());
        const next = (await response.json()) as ArtworkRender;
        setRender(next);
        setStatus(
          `${source?.name ?? "built-in wedding study"} · ${(next.blackCoverage * 100).toFixed(1)}% black`,
        );
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          setStatus(error instanceof Error ? error.message : String(error));
        }
      } finally {
        if (!controller.signal.aborted) setRendering(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [source, options]);

  const filename = useMemo(() => {
    const slug = (options.title || source?.name || "wedding-artwork")
      .replace(/\.[^.]+$/, "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48);
    return slug || "wedding-artwork";
  }, [options.title, source?.name]);

  const print = async () => {
    if (!render) return;
    if (!canPrint) {
      const handoff = new URL("/local-print/artwork", LOCAL_TIXPIX);
      handoff.searchParams.set("url", new URL(render.url, window.location.origin).href);
      handoff.searchParams.set("count", String(count));
      window.open(handoff, "_blank", "noopener");
      setStatus("opened the local print confirmation…");
      return;
    }
    setPrinting(true);
    setStatus(`printing ${count}× on the attached QL-700…`);
    try {
      const response = await fetch("/api/artwork/print", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: render.id, count }),
      });
      if (!response.ok) throw new Error(await response.text());
      setStatus(`printed ${count}×`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setPrinting(false);
    }
  };

  const savePng = async () => {
    if (!render) return;
    setStatus("saving exact one-bit PNG…");
    try {
      const response = await fetch("/api/artwork/save-png", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: render.id, filename: `${filename}.png` }),
      });
      if (!response.ok) throw new Error(await response.text());
      setStatus(await finishSave(response, `${filename}.png`));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  };

  const saveProject = async () => {
    setStatus("saving editable project…");
    const project: SavedProject = {
      format: "tixpix-artwork",
      version: 1,
      savedAt: new Date().toISOString(),
      options,
      source: source
        ? { name: source.name, type: source.type, dataUrl: await fileToDataUrl(source) }
        : null,
    };
    try {
      const response = await fetch("/api/artwork/save-project", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ filename: `${filename}.tixpix`, project }),
      });
      if (!response.ok) throw new Error(await response.text());
      setStatus(await finishSave(response, `${filename}.tixpix`, "editable "));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  };

  const openProject = async (file: File | undefined) => {
    if (!file) return;
    try {
      const project = JSON.parse(await file.text()) as Partial<SavedProject>;
      if (project.format !== "tixpix-artwork" || project.version !== 1 || !project.options) {
        throw new Error("not a Tixpix artwork project");
      }
      setOptions({ ...DEFAULT_OPTIONS, ...project.options });
      setSource(project.source ? dataUrlToFile(project.source) : null);
      setStatus(`opened ${file.name}`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      if (projectInput.current) projectInput.current.value = "";
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="border-b px-6 py-3 flex items-center gap-3">
        <div className="text-xs text-muted-foreground flex-1 truncate">{status}</div>
        <input
          ref={projectInput}
          type="file"
          accept=".tixpix,application/json"
          className="hidden"
          onChange={(event) => openProject(event.target.files?.[0])}
        />
        <Button variant="outline" onClick={() => projectInput.current?.click()}>
          Open project
        </Button>
        <Button variant="outline" onClick={saveProject}>
          Save project
        </Button>
        <Button variant="outline" onClick={savePng} disabled={!render || rendering}>
          Save PNG
        </Button>
        <span className="text-xs text-muted-foreground">62 mm continuous</span>
        <Input
          type="number"
          min={1}
          max={20}
          className="w-20"
          value={count}
          onChange={(event) => setCount(Math.max(1, Math.min(20, +event.target.value || 1)))}
          aria-label="Copies"
        />
        <Button onClick={print} disabled={!render || rendering || printing}>
          {printing
            ? "Printing…"
            : canPrint
              ? "Print artwork"
              : "Open local print"}
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-[420px_1fr] min-h-0">
        <aside className="border-r p-4 overflow-y-auto space-y-5">
          <section className="space-y-2">
            <Label htmlFor="artwork-file">Portrait or illustration</Label>
            <Input
              id="artwork-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => setSource(event.target.files?.[0] ?? null)}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                PNG with a transparent background works best.
              </p>
              {source && (
                <Button variant="ghost" size="sm" onClick={() => setSource(null)}>
                  Use sample
                </Button>
              )}
            </div>
          </section>

          <section className="space-y-3 rounded-md border p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Inscription
            </div>
            <div className="space-y-1">
              <Label htmlFor="artwork-title" className="text-xs">Names or title</Label>
              <Input
                id="artwork-title"
                value={options.title}
                onFocus={() => setActiveLine("title")}
                onChange={(event) => patch("title", event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="artwork-details" className="text-xs">Date, place, or detail</Label>
              <Input
                id="artwork-details"
                value={options.details}
                onFocus={() => setActiveLine("details")}
                onChange={(event) => patch("details", event.target.value)}
              />
            </div>
            <ArtworkTypography
              options={options}
              activeLine={activeLine}
              setActiveLine={setActiveLine}
              patch={patch}
            />
            <div className="space-y-1 pt-1">
              <Label htmlFor="artwork-height" className="text-xs">Label length in pixels</Label>
              <Input
                id="artwork-height"
                type="number"
                min={600}
                max={1800}
                step={50}
                value={options.height}
                onChange={(event) => patch("height", Math.max(600, Math.min(1800, +event.target.value || 900)))}
              />
              <p className="text-[11px] text-muted-foreground">
                {Math.round((options.height / 300) * 25.4)} mm long at 300 dpi
              </p>
            </div>
          </section>

          <section className="space-y-3 rounded-md border p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Composition
            </div>
            <RangeField label="Scale" value={options.scale} min={0.5} max={2} step={0.05} onChange={(value) => patch("scale", value)} />
            <RangeField label="Horizontal" value={options.offsetX} min={-240} max={240} step={5} suffix="px" onChange={(value) => patch("offsetX", value)} />
            <RangeField label="Vertical" value={options.offsetY} min={-240} max={240} step={5} suffix="px" onChange={(value) => patch("offsetY", value)} />
            <RangeField label="Rotation" value={options.rotation} min={-20} max={20} step={1} suffix="°" onChange={(value) => patch("rotation", value)} />
          </section>

          <section className="space-y-3 rounded-md border p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              One-bit treatment
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={options.method}
              onValueChange={(value) => value && patch("method", value as DitherMethod)}
              className="grid grid-cols-3"
            >
              <ToggleGroupItem value="atkinson">Atkinson</ToggleGroupItem>
              <ToggleGroupItem value="floyd-steinberg">Floyd</ToggleGroupItem>
              <ToggleGroupItem value="bayer">Bayer</ToggleGroupItem>
            </ToggleGroup>
            <RangeField label="Exposure" value={options.exposure} min={-1.5} max={1.5} step={0.05} onChange={(value) => patch("exposure", value)} />
            <RangeField label="Contrast" value={options.contrast} min={0.5} max={2.5} step={0.05} onChange={(value) => patch("contrast", value)} />
            <RangeField label="Gamma" value={options.gamma} min={0.5} max={2} step={0.05} onChange={(value) => patch("gamma", value)} />
            <RangeField label="Threshold" value={options.threshold} min={0.25} max={0.75} step={0.01} onChange={(value) => patch("threshold", value)} />
          </section>
        </aside>

        <main className="overflow-auto bg-muted p-8 min-h-0">
          <div className="sticky top-0 z-10 mx-auto mb-6 flex w-fit items-center gap-3 rounded-lg border bg-background/95 p-1.5 shadow-sm backdrop-blur">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={previewMode}
              onValueChange={(value) => value && setPreviewMode(value as PreviewMode)}
            >
              <ToggleGroupItem value="pixels">Exact pixels</ToggleGroupItem>
              <ToggleGroupItem value="print">Print simulation</ToggleGroupItem>
            </ToggleGroup>
            <span className="pr-2 text-[11px] text-muted-foreground">
              {previewMode === "pixels" ? "binary PNG sent to printer" : "approximate tape, dot gain, and thermal density"}
            </span>
          </div>
          {previewMode === "pixels" ? (
            <ExactPreview render={render} options={options} />
          ) : (
            <PrintSimulation render={render} options={options} />
          )}
        </main>
      </div>
    </div>
  );
}

function ArtworkTypography({
  options,
  activeLine,
  setActiveLine,
  patch,
}: {
  options: ArtworkOptions;
  activeLine: TextLine;
  setActiveLine: (line: TextLine) => void;
  patch: <K extends keyof ArtworkOptions>(key: K, value: ArtworkOptions[K]) => void;
}) {
  const prefix = activeLine === "title" ? "title" : "details";
  const fontKey = `${prefix}Font` as "titleFont" | "detailsFont";
  const sizeKey = `${prefix}Size` as "titleSize" | "detailsSize";
  const smallCapsKey = `${prefix}SmallCaps` as "titleSmallCaps" | "detailsSmallCaps";
  const upperKey = `${prefix}Upper` as "titleUpper" | "detailsUpper";
  const flags = [options[smallCapsKey] && "smallcaps", options[upperKey] && "upper"].filter(Boolean) as string[];

  return (
    <div className="space-y-3 rounded-md bg-muted/55 p-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-muted-foreground">Typography for</Label>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={activeLine}
          onValueChange={(value) => value && setActiveLine(value as TextLine)}
        >
          <ToggleGroupItem value="title">Title</ToggleGroupItem>
          <ToggleGroupItem value="details">Detail</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="flex items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={options[fontKey]}
          onValueChange={(value) => value && patch(fontKey, value as FontFamily)}
          className="flex-1"
        >
          <ToggleGroupItem value="equity" className="flex-1 font-[Equity]">Equity</ToggleGroupItem>
          <ToggleGroupItem value="berkeley" className="flex-1 font-mono">Berkeley</ToggleGroupItem>
        </ToggleGroup>
        <Input
          type="number"
          min={16}
          max={110}
          className="w-20"
          value={options[sizeKey]}
          onChange={(event) => patch(sizeKey, Math.max(16, Math.min(110, +event.target.value || 16)))}
          aria-label={`${activeLine} font size`}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <ToggleGroup
          type="multiple"
          size="sm"
          variant="outline"
          value={flags}
          onValueChange={(values: string[]) => {
            patch(smallCapsKey, values.includes("smallcaps"));
            patch(upperKey, values.includes("upper"));
          }}
        >
          <ToggleGroupItem value="smallcaps" className="font-variant-caps-small-caps tracking-wide">Sᴄ</ToggleGroupItem>
          <ToggleGroupItem value="upper" className="uppercase tracking-wide">Aa</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={options.textAlign}
          onValueChange={(value) => value && patch("textAlign", value as TextAlign)}
          aria-label="Text alignment"
        >
          <ToggleGroupItem value="left">Left</ToggleGroupItem>
          <ToggleGroupItem value="center">Center</ToggleGroupItem>
          <ToggleGroupItem value="right">Right</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={options.textPosition}
          onValueChange={(value) => value && patch("textPosition", value as TextPosition)}
          className="grid grid-cols-2"
        >
          <ToggleGroupItem value="top">Top</ToggleGroupItem>
          <ToggleGroupItem value="bottom">Bottom</ToggleGroupItem>
        </ToggleGroup>
        <Button
          type="button"
          size="sm"
          variant={options.showDivider ? "secondary" : "outline"}
          onClick={() => patch("showDivider", !options.showDivider)}
        >
          {options.showDivider ? "Divider on" : "Divider off"}
        </Button>
      </div>
    </div>
  );
}

function ExactPreview({ render, options }: { render: ArtworkRender | null; options: ArtworkOptions }) {
  return (
    <div className="mx-auto w-fit min-w-[696px]">
      {render ? (
        <img
          src={render.url}
          alt="Exact one-bit artwork preview"
          width={render.width}
          height={render.height}
          className="bg-white shadow-xl"
          style={{ imageRendering: "pixelated" }}
        />
      ) : (
        <PreviewPlaceholder height={options.height} />
      )}
    </div>
  );
}

function PrintSimulation({ render, options }: { render: ArtworkRender | null; options: ArtworkOptions }) {
  const physicalLength = Math.round((options.height / 300) * 25.4);
  return (
    <div className="mx-auto flex min-h-full w-full min-w-[560px] flex-col items-center justify-center py-8">
      <div className="mb-4 text-center">
        <div className="text-xs font-medium">62 × {physicalLength} mm continuous label</div>
        <div className="text-[11px] text-muted-foreground">shown at approximately 2× physical size</div>
      </div>
      <div
        className="thermal-label"
        style={{ width: 468, aspectRatio: `${62} / ${physicalLength}` }}
      >
        {render ? (
          <img src={render.url} alt="Approximate thermal print preview" className="thermal-ink" />
        ) : (
          <div className="h-full w-full animate-pulse bg-stone-100" />
        )}
      </div>
      <div className="mt-4 max-w-md text-center text-[11px] leading-relaxed text-muted-foreground">
        Simulation softens individual dots, adds slight thermal spread and paper tone. The exact result still varies with stock, printer heat, head condition, and feed direction.
      </div>
    </div>
  );
}

function PreviewPlaceholder({ height }: { height: number }) {
  return (
    <div
      className="w-[696px] bg-white shadow-xl flex items-center justify-center text-muted-foreground"
      style={{ height }}
    >
      rendering…
    </div>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  step,
  suffix = "",
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="grid grid-cols-[88px_1fr_62px] items-center gap-2">
      <Label className="text-xs">{label}</Label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(+event.target.value)}
        className="w-full accent-black"
      />
      <span className="text-right text-xs tabular-nums text-muted-foreground">
        {Number.isInteger(step) ? value : value.toFixed(2)}{suffix}
      </span>
    </div>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("could not read source image"));
    reader.readAsDataURL(file);
  });
}

function dataUrlToFile(source: NonNullable<SavedProject["source"]>): File {
  const [header, encoded] = source.dataUrl.split(",", 2);
  if (!header || !encoded) throw new Error("project source image is invalid");
  const mime = /^data:([^;]+)/.exec(header)?.[1] ?? source.type ?? "image/png";
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  return new File([bytes], source.name || "artwork.png", { type: mime });
}

async function finishSave(response: Response, filename: string, prefix = "") {
  if (response.headers.get("content-disposition")?.includes("attachment")) {
    downloadBlob(await response.blob(), filename);
    return `downloaded ${prefix}${filename}`;
  }
  const result = (await response.json()) as { path: string };
  return `saved ${prefix}${result.path}`;
}
